import { useTranslation } from "@shared/utils/internationalization";
import React, { useMemo } from "react";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";

import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import { getPriceRange } from "@shared/modules/financials/utils/price-utils";

import moment from "moment-timezone";
import {
  NestedPublicVenueSerializer,
  PublicBaseEventSerializer,
} from "@app-contexts/public/features/event-detail/data/types/events";
import { PublicVenueSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";
import { useTimer } from "@ui-kit/feedback/timer/useTimer";
import { UserBasedTicketBasketHoldsSerializer } from "@app-contexts/user/features/basket/data/types/basket";
import DetailPageButton from "@/components/molecules/purchases/DetailPageButton";

interface CallToActionProps {
  eventData?: PublicBaseEventSerializer;
  membershipGroupData?: PublicMembershipGroupSerializer;
  venue: NestedPublicVenueSerializer | PublicVenueSerializer;
  openModal: () => void;
  holdDetailsData?: UserBasedTicketBasketHoldsSerializer;
  /** @deprecated No longer used -- responsive switching is CSS-based now */
  isSmallerThanSm?: boolean;
}

/**
 * Footer with call to action button. Used on regular event detail pages and membership detail pages.
 * Displays a button that opens the ticket list modal or membership list modal.
 */
const CallToAction: React.FC<CallToActionProps> = ({
  venue,
  openModal,
  isSmallerThanSm: _isSmallerThanSm,
  eventData = undefined,
  membershipGroupData = undefined,
  holdDetailsData = null,
}) => {
  const { t } = useTranslation("purchases");

  const priceRangeString = useMemo(() => {
    let priceRange = "";

    if (eventData) {
      priceRange = eventData?.no_ticket_types
        ? t("Free")
        : getPriceRange({
            items: eventData?.ticket_types,
            allInPricing: Boolean(eventData?.venue?.all_in_pricing_is_enabled),
            venueCurrency: eventData?.venue?.currency,
            shouldRound: false,
          });
    } else if (membershipGroupData) {
      priceRange = getPriceRange({
        items: membershipGroupData?.membership_levels,
        allInPricing: Boolean(venue?.all_in_pricing_is_enabled),
        venueCurrency: venue?.currency,
        shouldRound: false,
      });
    }

    return priceRange;
  }, [eventData, membershipGroupData, venue, t]);

  const hidePriceText = !priceRangeString;
  const eventSaleStartsOn = eventData?.event_sale_starts_on;
  const saleStartsOnTz = moment
    .tz(eventSaleStartsOn, eventData?.timezone)
    .toDate();
  const [timeRemaining] = useTimer(saleStartsOnTz);

  const saleStartsOnPassed = eventSaleStartsOn
    ? new Date().valueOf() >= new Date(eventSaleStartsOn).valueOf()
    : true;

  return (
    <Flex
      display={{ base: "flex", lg: "none" }}
      direction="column"
      borderBottom="1px solid"
      borderBottomColor="border"
      w="100%"
      h="auto"
      pb="env(safe-area-inset-bottom)"
      position="sticky"
      zIndex="sticky"
      bottom="none"
    >
      <Flex
        direction="row"
        p={{ base: "10px 20px", sm: "10px 40px" }}
        justifyContent="space-between"
        alignContent="center"
        alignItems="center"
        hidden={eventData?.no_ticket_types}
        display={eventSaleStartsOn && timeRemaining > 0 ? "none" : "flex"}
      >
        <Flex
          direction="column"
          data-testid="price-range"
          display={{ base: "none", sm: "flex" }}
        >
          <Text hidden={hidePriceText} fontSize="xs">
            {t("Price")}
          </Text>
          <Text hidden={hidePriceText} fontSize="sm" fontWeight="bold">
            {priceRangeString}
          </Text>
        </Flex>
        <DetailPageButton
          saleStartsOnPassed={saleStartsOnPassed}
          eventData={eventData}
          membershipGroupData={membershipGroupData}
          openModal={openModal}
          holdDetailsData={holdDetailsData}
          props={{
            "data-testid": "call-to-action-button",
            colorPalette: "secondary",
            minHeight: "50px",
            width: { base: "100%", sm: "50%" },
            maxWidth: { base: "100%", sm: "364px" },
            display: eventSaleStartsOn && timeRemaining > 0 ? "none" : "block",
          }}
        />
      </Flex>
    </Flex>
  );
};

export default CallToAction;
